import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  minBreakpointQuery,
  standardColours,
} from '../styles';
import { Button } from './ui';

const StyledListingCtaCard = styled.article`
  position: relative;
  padding: 30px;
  color: ${({ headingTextColour }) => headingTextColour.hex};
  background-color: ${({ backgroundColour }) => backgroundColour.hex};
  border-radius: 15px;
  height: 100%;

  ${minBreakpointQuery.xlarge`
    padding: 40px 45px;
  `}

  ${({ $isVenuePage }) => {
    if ($isVenuePage) {
      return css`
        aspect-ratio: 7/8;
      `;
    }
  }}
`;

const StyledBackgroundImage = styled(GatsbyImage)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: ${({ backgroundImageOpacity }) => backgroundImageOpacity / 100};
`;

const StyledContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledIcon = styled.img`
  margin-bottom: 20px;
  width: ${({ iconWidth }) => (iconWidth ? iconWidth : '64')}px;
`;

const StyledHeading = styled.h3`
  ${fontSize(29)}
  line-height: 1.2;

  ${minBreakpointQuery.small`
    ${fontSize(32)}
  `}

  ${minBreakpointQuery.large`
    ${fontSize(35)}
  `}

  strong {
    color: ${brandColours.senary};
  }
`;

const StyledTextButton = styled.div`
  margin-top: auto;
`;

const StyledText = styled.p`
  margin-top: 20px;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
  color: ${brandColours.primary};
  background-color: ${standardColours.white};

  &:hover {
    color: ${standardColours.white};
    background-color: ${brandColours.octonary};
  }
`;

const ListingCtaCard = ({
  icon,
  iconWidth,
  heading,
  text,
  headingTextColour,
  link,
  backgroundColour,
  backgroundImage,
  backgroundImageOpacity,
  isVenuePage,
}) => (
  <StyledListingCtaCard
    headingTextColour={headingTextColour}
    backgroundColour={backgroundColour}
    $isVenuePage={isVenuePage}
  >
    {backgroundImage && (
      <StyledBackgroundImage
        image={backgroundImage.gatsbyImageData}
        alt={backgroundImage.alt}
        backgroundImageOpacity={backgroundImageOpacity}
      />
    )}
    <StyledContent>
      {icon && (
        <StyledIcon
          src={icon.url}
          alt={icon.alt}
          loading="lazy"
          iconWidth={iconWidth}
        />
      )}
      <StyledHeading
        dangerouslySetInnerHTML={{
          __html: heading.replace(/(<p>|<\/p>)/g, ''),
        }}
        {...(isVenuePage && { as: 'h4' })}
      />
      {(text || link) && (
        <StyledTextButton>
          {text && <StyledText>{text}</StyledText>}
          {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
        </StyledTextButton>
      )}
    </StyledContent>
  </StyledListingCtaCard>
);

export default ListingCtaCard;

export const ListingCtaCardFragment = graphql`
  fragment ListingCtaCardFragment on DatoCmsListingCallToAction {
    icon {
      url
      alt
    }
    iconWidth
    heading
    text
    headingTextColour {
      hex
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    backgroundColour {
      hex
    }
    backgroundImage {
      gatsbyImageData(
        width: 460
        height: 460
        imgixParams: { w: "460", h: "460" }
      )
      alt
    }
    backgroundImageOpacity
    internal {
      type
    }
  }
`;
